import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import useEventTracking from '@hooks/useEventTracking';
import useStickyBarHeight from '@hooks/useStickyBarHeight';
import useWindowSize from '@hooks/useWindowSize';
import { TrackingLocation } from '@tracking';
import { ClickTarget, ContentType, ImpressionElement, click, impression } from '@tracking';
import { WIDTH_SM } from '@utils/constants';

import Header from './Header';
import StreamPlayer from './StreamPlayer';
import { COOKIE_MAX_AGE, COOKIE_NAME } from './constants';

const cookies = new Cookies();

interface TITVPopoutProps {
  currentPage?: string;
  id: number;
  videoId?: string;
  title: string;
}

const TITVPopout = ({ videoId, currentPage, title, id }: TITVPopoutProps) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [hasCheckedCookie, setHasCheckedCookie] = useState(false);
  const { width: windowWidth } = useWindowSize();
  const { height: stickyBarHeight, isVisible: stickyBarVisible } = useStickyBarHeight({
    isMinimized,
    isMobile: windowWidth < WIDTH_SM,
  });

  const { trackEvent, Track } = useEventTracking({
    contentType: ContentType.TITVStream,
    location: TrackingLocation.TITVPopout,
    contentId: id,
    page: currentPage,
  });

  useEffect(() => {
    try {
      const wasClosed = cookies.get(COOKIE_NAME);
      if (wasClosed === 'true') {
        setIsClosed(true);
      }
    } catch (error) {
      console.warn('Failed to read cookie:', error);
    } finally {
      setHasCheckedCookie(true);
    }
  }, []);

  useEffect(() => {
    if (windowWidth < WIDTH_SM) {
      setIsMinimized(true);
    } else {
      setIsMinimized(false);
    }
  }, [windowWidth]);

  useEffect(() => {
    if (hasCheckedCookie && videoId) {
      trackEvent(impression({ impressionElement: ImpressionElement.TITVPopout, context: title }));
    }
  }, [trackEvent, hasCheckedCookie, videoId, title]);

  const handleClose = useCallback(() => {
    cookies.set(COOKIE_NAME, 'true', {
      path: '/',
      maxAge: COOKIE_MAX_AGE,
    });
    trackEvent(click({ clickTarget: ClickTarget.Close }));
    setIsClosed(true);
  }, [trackEvent]);

  const toggleMinimized = useCallback(() => {
    setIsMinimized((prev) => !prev);
    trackEvent(click({ clickTarget: ClickTarget.Minimize }));
  }, [trackEvent]);

  if (!hasCheckedCookie || !videoId || isClosed) {
    return null;
  }

  return (
    <Track>
      <aside
        className={clsx(
          'fixed right-0 z-50 m-0 flex min-w-full flex-col bg-white text-white shadow-pro-sm transition-[bottom] duration-300 ease-in-out sm:m-4 sm:min-w-[388px] dark:bg-black',
          {
            'bottom-2': !stickyBarVisible,
          }
        )}
        data-testid="titv-popout"
        style={
          {
            '--sticky-bar-height': `${stickyBarHeight}px`,
            bottom: stickyBarVisible ? `calc(var(--sticky-bar-height) + 8px)` : undefined,
          } as React.CSSProperties
        }
      >
        <Header
          isMinimized={isMinimized}
          toggleMinimized={toggleMinimized}
          handleClose={handleClose}
        />
        <div
          className={clsx('p-2', {
            'hidden p-0': isMinimized,
          })}
        >
          <StreamPlayer videoId={videoId} isMinimized={isMinimized} />
        </div>
      </aside>
    </Track>
  );
};

export default TITVPopout;
